import { useMemo, useEffect, useState } from 'react';
import moment from 'moment';
import { Col, Row, Form, Badge } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { Button } from 'components';
import { DeleteIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import { EyeOutlined } from '@ant-design/icons';

import { useParams } from 'react-router-dom';

import CandidatesApi from 'libs/api/cv/candidates';
import { useApi } from 'hooks/useApi';
import UIHelper from '../../../../helpers/UIHelper';
import SsoUsers from '../../../../libs/api/ssoUsers';
import { useNavigate } from '../../../../hooks/useNavigate';
import CVFilters from '../../../../libs/api/cv/cv';
import LinkWithParams from '../../../../components/linkWithParams';
import { useRecoilValue } from 'recoil';
import { getShops, getInactiveShops } from '../../../../hooks/useShops';
import { getShoppingCenters } from '../../../../hooks/useUser';

const api = new CandidatesApi();
const usersApi = new SsoUsers();
const filtersApi = new CVFilters();

export const statusColors = {
  Attivo: 'green',
  Disattivo: 'red',
};

/**
 *
 * @param {boolean} isMainPage - true if the page loads all items, false if just one
 * @returns {{
 *  columns: any,
 *  data: any,
 *  singleItem: any,
 *  loading: boolean,
 *  updateInProcess: boolean,
 *  setUpdateInProcess: func,
 *  onDelete: func,
 *  onChangeTableFilter: func
 * }} state and callbacks for ExperienceYears page
 */
const useCandidates = (isMainPage = true) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { id } = useParams();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const shops = useRecoilValue(getShops);
  const inactiveShops = useRecoilValue(getInactiveShops);
  const shoppingCenters = useRecoilValue(getShoppingCenters);
  const [filtersData, setFiltersData] = useState({
    vacancy_categories: [],
    experience_years: [],
    birthday_range: [],
    contract_types: [],
    regions: [],
    status: [],
    vacancy_application_types: [],
    vacancy_languages: [],
  });
  const {
    loading,
    data,
    singleItem,
    setUpdateInProcess,
    fetchList,
    onChangeTableFilter,
    updateInProcess,
    handleDownloadExcel,
    filtersFromBack,
  } = useApi({
    resourceKey: 'candidates',
    singleResourceKey: 'candidate',
    api,
    isMainPage,
  });

  const columns = useMemo(() => {
    return [
      {
        title: t('Annuncio'),
        dataIndex: 'vacancies_count',
        sorter: () => {},
      },
      {
        title: t('ID Candidato'),
        dataIndex: 'id',
        sorter: () => {},
      },
      {
        title: t('Cognome'),
        dataIndex: 'surname',
        sorter: () => {},
        render: (data, row) => row?.hide_surname,
      },
      {
        title: t('Nome'),
        dataIndex: 'name',
        sorter: () => {},
        render: (data, row) => row?.hide_name,
      },
      {
        title: t('Settore'),
        dataIndex: 'vacancy_application_types.title',
        sorter: () => {},
        render: (data, row) => row?.vacancy_application_type?.title,
      },
      {
        title: t('Profilo candidato'),
        dataIndex: 'vacancy_category.title',
        sorter: () => {},
        render: (data, row) => row?.vacancy_category?.title,
      },
      {
        title: t('Sesso'),
        dataIndex: 'sex',
        sorter: () => {},
        render: (data, row) => row?.sex_short,
      },
      {
        title: t('Fascia d’eta'),
        dataIndex: 'birthday_range',
        sorter: () => {},
        render: (data, row) => row?.birthday_range,
      },
      {
        title: t('Anni esp'),
        dataIndex: 'experience_years.title',
        sorter: () => {},
        render: (data, row) => row?.experience_year?.title,
      },
      {
        title: t('Dispon. contratto'),
        dataIndex: 'contract_types.title',
        sorter: () => {},
        render: (data, row) => row?.contract_type?.title,
      },
      {
        title: t('Provincia'),
        dataIndex: 'regions.title',
        sorter: () => {},
        render: (data, row) => row?.region?.title,
      },
      {
        title: t('Lingua 1'),
        dataIndex: 'vacancy_languages.title',
        sorter: () => {},
        render: (data, row) => row?.vacancy_language?.title,
      },
      {
        title: t('Livello lingua 1'),
        dataIndex: 'vacancy_language_level',
        sorter: () => {},
      },
      {
        title: t('Lingua 2'),
        dataIndex: 'vacancy_language2s_users.title',
        sorter: () => {},
        render: (data, row) => row?.vacancy_language2?.title,
      },
      {
        title: t('Livello lingua 2'),
        dataIndex: 'vacancy_language2_level',
        sorter: () => {},
      },
      {
        title: t('Stato'),
        dataIndex: 'status',
        sorter: () => {},
        render: (data, row) => <Badge color={statusColors[data]} text={data} />,
      },
      {
        title: t('Wishlist'),
        dataIndex: 'wishlists_count',
        sorter: () => {},
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 200,
        fixed: 'right',
        render: (data, row) => (
          <Row
            gutter={16}
            justify={'center'}
            onClick={(e) => e.stopPropagation()}
          >
            <Col>
              <LinkWithParams to={`${pathname}/${row.id}/view`}>
                <Button type={'primary'} icon={<EyeOutlined />}>
                  {t('APRI')}
                </Button>
              </LinkWithParams>
            </Col>
            {row.can_destroy !== false && (
              <Col>
                <Link to={`${pathname}/${row.id}/delete`}>
                  <Button type={'red'} icon={<DeleteIcon />} />
                </Link>
              </Col>
            )}
          </Row>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const filters = useMemo(
    () => [
      {
        title: t('Outlet Village'),
        type: 'select',
        name: 'by_shopping_center',
        values: shoppingCenters.map((item) => ({
          title: item.title,
          value: item.id,
        })),
      },
      {
        title: t('Annuncio'),
        type: 'checkbox',
        name: 'with_vacancy',
        paramValue: 'true',
        notCheckedParamValue: 'false',
      },
      {
        title: t('Insegna'),
        type: 'select',
        name: 'by_company_id',
        isSearch: true,
        values: shops.map((i) => {
          const find = filtersFromBack?.companies?.find(
            (j) => j.value === i.value,
          );
          let count = 0;
          if (find) {
            count = find.count;
          }
          return {
            ...i,
            title: `${i.title} (${count})`,
          };
        }),
        inactiveValues: inactiveShops.map((i) => {
          const find = filtersFromBack?.companies?.find(
            (j) => j.value === i.value,
          );
          let count = 0;
          if (find) {
            count = find.count;
          }
          return {
            ...i,
            title: `${i.title} (${count})`,
          };
        }),
      },
      {
        title: t('Cerca'),
        type: 'input',
        name: 'by_text',
        values: [],
        isActiveSearch: true,
      },
      {
        title: t('Settore'),
        type: 'select',
        name: 'by_app_type',
        values: filtersData.vacancy_application_types.map((i) => {
          const find = filtersFromBack?.vacancy_application_types?.find(
            (j) => j.value == i.value,
          );
          let count = 0;
          if (find) {
            count = find.count;
          }
          return {
            value: i.value,
            title: `${i.title} (${count})`,
          };
        }),
      },
      {
        title: t('Profilo candidato'),
        type: 'select',
        name: 'by_vacancy_category',
        values: filtersData.vacancy_categories.map((i) => {
          const find = filtersFromBack?.vacancy_categories?.find(
            (j) => j.value == i.value,
          );
          let count = 0;
          if (find) {
            count = find.count;
          }
          return {
            value: i.value,
            title: `${i.title} (${count})`,
          };
        }),
      },
      {
        title: t('Sesso'),
        type: 'select',
        name: 'by_sex',
        values: [
          { value: 1, title: 'M' },
          { value: 2, title: 'F' },
        ].map((i) => {
          const find = filtersFromBack?.sex?.find((j) => j.value === i.value);
          let count = 0;
          if (find) {
            count = find.count;
          }
          return {
            ...i,
            title: `${i.title} (${count})`,
          };
        }),
      },
      {
        title: t('Fascia di età'),
        type: 'select',
        name: 'by_range_birthday',
        values: filtersData.birthday_range.map((i) => {
          const find = filtersFromBack?.birthday?.find(
            (j) => j.value === i.value,
          );
          let count = 0;
          if (find) {
            count = find.count;
          }
          return {
            value: i.value,
            title: `${i.title} (${count})`,
          };
        }),
      },
      {
        title: t('Anni di esperienza'),
        type: 'select',
        name: 'by_experience_years_id',
        values: filtersData.experience_years.map((i) => {
          const find = filtersFromBack?.experience_years?.find(
            (j) => j.value == i.value,
          );
          let count = 0;
          if (find) {
            count = find.count;
          }
          return {
            title: `${i.title} (${count})`,
            value: i.value,
          };
        }),
      },
      {
        title: t('Disponibilità contratto'),
        type: 'select',
        name: 'by_contract_type_id',
        values: filtersData.contract_types.map((i) => {
          const find = filtersFromBack?.contract_types?.find(
            (j) => j.value === i.value,
          );
          let count = 0;
          if (find) {
            count = find.count;
          }
          return {
            value: i.value,
            title: `${i.title} (${count})`,
          };
        }),
      },
      {
        title: t('Provincia'),
        type: 'select',
        name: 'by_region_id',
        isSearch: true,
        values: filtersData.regions.map((i) => {
          const find = filtersFromBack?.region?.find(
            (j) => j.value === i.value,
          );
          let count = 0;
          if (find) {
            count = find.count;
          }
          return {
            value: i.value,
            title: `${i.title} (${count})`,
          };
        }),
      },
      {
        title: t('Lingua 1'),
        type: 'select',
        name: 'by_vacancy_language_id',
        values: filtersData.vacancy_languages.map((i) => {
          const find = filtersFromBack?.vacancy_languages?.find(
            (j) => j.value == i.value,
          );
          let count = 0;
          if (find) {
            count = find.count;
          }
          return {
            title: `${i.title} (${count})`,
            value: i.value,
          };
        }),
      },
      {
        title: t('Livello lingua 1'),
        type: 'input',
        name: 'by_vacancy_language_level',
        values: [],
      },
      {
        title: t('Lingua 2'),
        type: 'select',
        name: 'by_vacancy_language2_id',
        values: filtersData.vacancy_languages.map((i) => {
          const find = filtersFromBack?.vacancy_languages2?.find(
            (j) => j.value == i.value,
          );
          let count = 0;
          if (find) {
            count = find.count;
          }
          return {
            title: `${i.title} (${count})`,
            value: i.value,
          };
        }),
      },
      {
        title: t('Livello lingua 2'),
        type: 'input',
        name: 'by_vacancy_language2_level',
        values: [],
      },
      {
        title: 'Attivo/disattivo',
        type: 'select',
        name: 'by_status',
        valueType: 'number',
        values: filtersData.status.map((i) => {
          const find = filtersFromBack?.status?.find((j) => j.value == i.value);
          let count = 0;
          if (find) {
            count = find.count;
          }
          return {
            value: +i.value,
            title: `${i.title} (${count})`,
          };
        }),
      },
    ],
    [t, filtersData, shops, inactiveShops, filtersFromBack, shoppingCenters],
  );

  useEffect(() => {
    if (id && !isMainPage && singleItem?.title) {
      form.setFields([
        {
          name: 'title',
          value: singleItem.title,
        },
      ]);
    }
  }, [singleItem, form, id, isMainPage]);

  const onDelete = async (id) => {
    setUpdateInProcess(true);
    try {
      await usersApi.destroy(id);
      navigate(UIHelper.getListRoute(pathname));
      fetchList();
    } catch (e) {
    } finally {
      setUpdateInProcess(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const filters = await filtersApi.list();
        setFiltersData(filters.data);
      } catch (e) {
        console.log(e);
      }
    })();
  }, []);

  return {
    columns,
    data,
    singleItem,
    filters,
    handleDownloadExcel,
    updateInProcess,
    loading,
    onChangeTableFilter,
    onDelete,
    pathname,
    navigate,
  };
};

export default useCandidates;
